import React, { useEffect, useState } from 'react';

import { MultiSelect, MultiSelectProps } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import useAutoCompleteRevenueCategories from 'hooks/revenueCategory/useAutoCompleteRevenueCategories';
import { RevenueCategoryAutoComplete } from 'types';

export interface IRevenueCategoryMultiSelectAutoComplete extends RevenueCategoryAutoComplete {}

interface RevenueCategoryMultiSelectAutoCompleteProps extends Omit<MultiSelectProps, 'data'> {
  values: IRevenueCategoryMultiSelectAutoComplete[];
  handleChange: (values: IRevenueCategoryMultiSelectAutoComplete[]) => void;
}

const RevenueCategoryMultiSelectAutoComplete = (
  props: RevenueCategoryMultiSelectAutoCompleteProps,
): JSX.Element => {
  const { values, handleChange, ...msProps } = props;
  const [autocompleteData, setAutocompleteData] = useState<IRevenueCategoryMultiSelectAutoComplete[]>([]);
  const [search, setSearch] = useState<string>('');
  const [debouncedSearchTerm] = useDebouncedValue<string>(search, 250);
  const { data, isSuccess } = useAutoCompleteRevenueCategories({
    search: debouncedSearchTerm,
  });

  const onChange = (selectedItems: string[]): void => {
    const selectedCategories = autocompleteData.filter(category =>
      selectedItems.includes(category.revenueCategoryId),
    );
    handleChange(selectedCategories);
  };

  useEffect(() => {
    if (isSuccess && data) {
      const updatedData = data.data.concat(
        values.filter(v => !data.data.some(category => category.revenueCategoryId === v.revenueCategoryId)),
      );
      setAutocompleteData(updatedData);
    }
  }, [isSuccess, data, values]);

  return (
    <MultiSelect
      placeholder='Select revenue categories'
      {...msProps}
      value={values.map(v => v.revenueCategoryId)}
      onChange={onChange}
      data={autocompleteData.map(category => ({
        value: category.revenueCategoryId,
        label: category.name,
      }))}
      clearable
      searchable
      searchValue={search}
      onSearchChange={setSearch}
      nothingFound='No revenue categories found'
    />
  );
};

export default RevenueCategoryMultiSelectAutoComplete;
